import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Search, Package, Plus, UserPlus } from "lucide-react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Badge } from "@/ui/badge";
import { Skeleton } from "@/ui/skeleton";
import { Pagination } from "@/components/Pagination";
import { useDebounce } from "@/hooks/useDebounce";
import { DataTable } from "@/components/DataTable";
import { formatVND, formatDate } from "@/lib/format";
import { usePrintOrderPage, type CskhPrintOrder, type PrintOrderSort } from "../api";
import {
  PRINT_STEPS,
  itemsOf,
  hasPendingDiscount,
  poPayBadge,
  poPayable,
  SELLER_NONE,
  SELLER_PORTAL,
  sellerKey,
  sellerLabel,
  sellerOptionsFromCounts,
  stepColor,
  stepLabel,
} from "../printOrderShared";
import { PrintOrderDiscountActions } from "./PrintOrderDiscountActions";

/**
 * PrintOrdersListView — danh sách đơn in dựng lại theo bản vẽ CEO duyệt
 * 01/09/2026.
 *
 * Đổi so với bản cũ (danh sách div tự dựng): bảng có cột thật · tìm theo tên
 * hoặc số · lọc theo bước hoặc theo đơn chưa thu tiền, kèm SỐ ĐẾM · ba kiểu
 * sắp xếp · tiền canh phải bằng chữ số đều · một hành động chính, phần còn lại
 * gom vào menu ⋯.
 *
 * Thứ tự mặc định là THEO BƯỚC (CEO chốt 2abc + mặc định theo bước): người
 * CSKH mở màn ra để biết "việc nào đang đứng ở khâu nào", không phải để tra
 * một đơn cụ thể — tra một đơn đã có ô tìm kiếm.
 *
 * Tìm, lọc, sắp, đếm và chia trang đều ở MÁY CHỦ (GET /print-orders?limit=…):
 * màn chỉ cầm một trang. Thứ tự đếm của máy chủ khớp đúng bản lọc cũ phía
 * trình duyệt nên số trên mỗi chip vẫn là số dòng hiện ra khi bấm nó.
 */

type SortKey = Exclude<PrintOrderSort, "">;

const SORTS: { key: SortKey; label: string }[] = [
  { key: "step", label: "Theo bước" },
  { key: "payable", label: "Phải thu" },
  { key: "updated", label: "Mới cập nhật" },
];

/** Giá trị lọc của chip "Chưa thu tiền" — không trùng mã bước nào. */
const UNPAID = "unpaid";

const PAGE_SIZE = 20;

/** StepPill — chấm màu + nhãn tiếng Việt. Chấm để phân biệt được khi in đen
 *  trắng hoặc với người khó phân biệt màu (không dựa vào MỖI màu). */
function StepPill({ step }: { step: string }) {
  const color = stepColor(step);
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold"
      style={{ background: `${color}18`, color, border: `1px solid ${color}40` }}
    >
      <span
        aria-hidden
        className="inline-block size-1.5 shrink-0 rounded-full"
        style={{ background: color }}
      />
      {stepLabel(step)}
    </span>
  );
}

/**
 * goodsSummary — mô tả hàng hoá của đơn.
 *
 * Đơn di trú từ ERP cũ KHÔNG mang theo chi tiết dòng hàng (đo 01/09/2026:
 * 11/11 đơn rỗng). Bản cũ in ra "0 sản phẩm · 0 đ" — đọc y như đơn trống
 * ruột do nhập sai. CEO chốt 01/09/2026 (đáp án a): không đi nhặt lại từ ERP
 * cũ, mà nói thẳng đây là đơn cũ không có chi tiết.
 */
function goodsSummary(po: CskhPrintOrder): { text: string; muted: boolean } {
  const n = itemsOf(po).length;
  if (n === 0 && po.legacy) return { text: "đơn cũ — không có chi tiết hàng", muted: true };
  if (n === 0) return { text: "chưa có sản phẩm", muted: true };
  return { text: `${n} sản phẩm`, muted: false };
}

export function PrintOrdersListView({
  canWrite,
  onOpen,
  onNew,
  onNewCustomer,
}: {
  canWrite: boolean;
  onOpen: (po: CskhPrintOrder) => void;
  onNew: () => void;
  onNewCustomer: () => void;
}) {
  const [q, setQ] = useState("");
  // "all", một mã bước, hoặc UNPAID — mỗi lúc chỉ bật một chip.
  const [filter, setFilter] = useState<string>("all");
  // "all" hoặc một khoá của `sellerKey` — lọc theo người chào, chồng lên chip
  // bước (CEO chốt câu 2a, 25/09/2026).
  const [seller, setSeller] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("step");
  const [page, setPage] = useState(0);
  const needle = useDebounce(q.trim(), 300);

  // Đổi bất kỳ bộ lọc nào là một tập kết quả mới — trang 3 của tập cũ vô nghĩa.
  const resetting =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(0);
    };

  const listQ = usePrintOrderPage({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    q: needle,
    step: filter === "all" || filter === UNPAID ? "" : filter,
    seller: seller === "all" ? "" : seller,
    unpaid: filter === UNPAID,
    sort,
  });
  const rows = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const counts = listQ.data?.stepCounts ?? {};
  const unpaidCount = listQ.data?.unpaidCount ?? 0;
  const bySellerCount = Object.values(counts).reduce((a, n) => a + n, 0);
  const nothingAtAll =
    !needle && filter === "all" && seller === "all" && total === 0 && !listQ.isFetching;

  // Lựa chọn suy từ số đếm của CHÍNH tập đang xem (máy chủ đếm trên kết quả
  // tìm, chưa cắt theo người). Người đang được chọn luôn ở lại trong danh
  // sách dù tìm kiếm đã cắt hết đơn của họ — biến mất khỏi ô chọn thì không ai
  // gỡ được bộ lọc đang bật.
  const sellers = useMemo(() => {
    const ds = sellerOptionsFromCounts(listQ.data?.sellerCounts ?? {});
    if (seller !== "all" && !ds.some((o) => o.key === seller)) {
      ds.push({ key: seller, label: sellerLabel(seller), count: 0 });
    }
    return ds;
  }, [listQ.data?.sellerCounts, seller]);

  const columns = useMemo<ColumnDef<CskhPrintOrder>[]>(
    () => [
      {
        id: "customer",
        header: "Khách",
        meta: { mobilePriority: "primary" },
        cell: ({ row }) => {
          const po = row.original;
          return (
            <button
              type="button"
              onClick={() => onOpen(po)}
              className="max-w-[22rem] text-left hover:underline"
            >
              <div className="truncate text-sm font-medium">
                {po.customer?.trim() || "Khách chưa có tên"}
              </div>
              {po.phone?.trim() ? (
                <div className="truncate text-xs text-muted-foreground tabular-nums">
                  {po.phone}
                </div>
              ) : null}
            </button>
          );
        },
      },
      {
        // Người chào đứng NGAY SAU tên khách: trưởng phòng soát hàng chờ đọc
        // theo cặp "khách ↔ ai chào", không phải dò sang tận cuối bảng.
        id: "seller",
        header: "Người chào",
        meta: { mobilePriority: "secondary", mobileLabel: "Người chào" },
        cell: ({ row }) => {
          // Cùng MỘT luật phân nhóm với ô lọc (`sellerKey`): ô bảng và bộ lọc
          // mà đọc dữ liệu theo hai cách là có ngày chúng nói khác nhau.
          const key = sellerKey(row.original);
          if (key === SELLER_PORTAL) {
            return (
              <Badge variant="outline" className="text-[10px]">
                Khách tự mua
              </Badge>
            );
          }
          if (key === SELLER_NONE) return <span className="text-xs text-muted-foreground">—</span>;
          return <span className="whitespace-nowrap text-xs">{key}</span>;
        },
      },
      {
        id: "goods",
        header: "Hàng hoá",
        meta: { mobilePriority: "secondary", mobileLabel: "Hàng hoá" },
        cell: ({ row }) => {
          const g = goodsSummary(row.original);
          return (
            <span className={`text-xs ${g.muted ? "text-muted-foreground italic" : ""}`}>
              {g.text}
            </span>
          );
        },
      },
      {
        id: "payable",
        header: () => <span className="block text-right">Phải thu</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Phải thu" },
        cell: ({ row }) => (
          <div className="text-right text-sm font-medium tabular-nums">
            {formatVND(poPayable(row.original))}
          </div>
        ),
      },
      {
        id: "pay",
        header: "Thu tiền",
        meta: { mobilePriority: "secondary", mobileLabel: "Thu tiền" },
        cell: ({ row }) => {
          const po = row.original;
          const badge = poPayBadge(po);
          return (
            <div className="space-y-0.5">
              <Badge variant={badge.variant} className="text-[10px]">
                {badge.label}
              </Badge>
              {/* Số tiền giảm + lý do hiện NGAY trên hàng chờ: người duyệt
                  quyết được mà không phải mở đơn ra đọc. */}
              {hasPendingDiscount(po) && (
                <div className="text-[11px] text-amber-700 dark:text-amber-500">
                  Giảm {formatVND(po.discount ?? 0)}
                  {po.discountNote?.trim() ? ` — ${po.discountNote.trim()}` : ""}
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: "step",
        header: "Bước",
        meta: { mobilePriority: "secondary", mobileLabel: "Bước" },
        cell: ({ row }) => <StepPill step={row.original.step} />,
      },
      {
        id: "updated",
        header: "Cập nhật",
        meta: { mobilePriority: "hidden" },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
            {formatDate(row.original.updatedAt)}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Hành động</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Hành động" },
        cell: ({ row }) => {
          const po = row.original;
          return (
            <div className="flex items-center justify-end gap-1">
              {/* Duyệt/Từ chối giảm giá đứng NGOÀI menu: nó chỉ hiện khi có
                  đúng một việc đang chờ người này quyết, giấu đi là bắt mở
                  từng đơn ra tìm. */}
              <PrintOrderDiscountActions po={po} />
              {/* Nút chữ thay menu ba chấm: menu chỉ có đúng một mục nên
                  bắt bấm hai lần, và chấm nhỏ khó trúng trên điện thoại. */}
              <Button size="sm" variant="outline" onClick={() => onOpen(po)}>
                Xem
              </Button>
            </div>
          );
        },
      },
    ],
    [onOpen],
  );

  if (listQ.isLoading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 md:min-w-[260px] md:flex-none">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={q}
            onChange={(e) => resetting(setQ)(e.target.value)}
            placeholder="Tìm tên khách hoặc số điện thoại"
            className="pl-8"
            aria-label="Tìm đơn in theo tên khách hoặc số điện thoại"
          />
        </div>
        <select
          aria-label="Lọc theo người chào"
          className="h-9 min-w-0 max-w-[14rem] rounded-md border bg-background px-2 text-sm"
          value={seller}
          onChange={(e) => resetting(setSeller)(e.target.value)}
        >
          <option value="all">Mọi người chào</option>
          {sellers.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label} ({o.count})
            </option>
          ))}
        </select>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* whitespace-nowrap khắp cụm: ở khổ điện thoại 390px cụm này bị bóp
              đến mức chữ vỡ GIỮA TỪ — nhãn thành "Sắp / xếp", nút thành "Theo
              / bước". Cho phép cả cụm xuống dòng, nhưng từng chữ thì không. */}
          <div className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
            <span>Sắp xếp</span>
            {SORTS.map((s) => (
              <Button
                key={s.key}
                size="sm"
                variant={sort === s.key ? "default" : "outline"}
                // Đặt lại NGAY TRÊN nút chứ không nhờ thẻ bọc: lớp nền của
                // Button có sẵn `whitespace-normal`, nó thắng giá trị thừa
                // hưởng từ cha nên nhãn vẫn vỡ giữa từ ("Theo / bước").
                className="whitespace-nowrap"
                onClick={() => resetting(setSort)(s.key)}
                aria-pressed={sort === s.key}
              >
                {s.label}
              </Button>
            ))}
          </div>
          {canWrite && (
            <>
              <Button size="sm" variant="outline" onClick={onNewCustomer}>
                <UserPlus className="size-4" /> Tạo khách mới
              </Button>
              <Button size="sm" onClick={onNew}>
                <Plus className="size-4" /> Tạo đơn in
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Chip lọc theo bước — số đếm cho biết TRƯỚC KHI bấm là có bao nhiêu.
          "Chưa thu tiền" đứng cuối hàng, không chấm màu, đúng bản vẽ CEO chốt
          (Câu 1 = 1a, 16/09/2026). */}
      <div className="flex flex-wrap items-center gap-1.5">
        <FilterChip
          label="Tất cả"
          count={bySellerCount}
          active={filter === "all"}
          onClick={() => resetting(setFilter)("all")}
        />
        {PRINT_STEPS.map((s) => (
          <FilterChip
            key={s.key}
            label={s.label}
            count={counts[s.key] ?? 0}
            color={s.color}
            active={filter === s.key}
            onClick={() => resetting(setFilter)(s.key)}
          />
        ))}
        <FilterChip
          label="Chưa thu tiền"
          count={unpaidCount}
          active={filter === UNPAID}
          onClick={() => resetting(setFilter)(UNPAID)}
        />
      </div>

      {listQ.isError ? (
        <div
          role="alert"
          className="rounded-md border p-6 text-center text-sm text-danger-text [border-color:var(--color-danger)] [background:var(--color-danger-bg)]"
        >
          {listQ.error instanceof Error ? listQ.error.message : "Không tải được danh sách đơn in."}{" "}
          <Button size="sm" variant="outline" onClick={() => listQ.refetch()}>
            Thử lại
          </Button>
        </div>
      ) : nothingAtAll ? (
        <div className="rounded-md border bg-surface p-8 text-center text-sm text-muted-foreground">
          <Package aria-hidden className="mx-auto mb-2 size-8 opacity-50" />
          Chưa có đơn in nào. Bấm “Tạo đơn in” để bắt đầu.
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            mobileVariant="stack"
            emptyMessage="Không có đơn in nào khớp bộ lọc đang chọn."
          />
          <Pagination
            total={total}
            pageSize={PAGE_SIZE}
            current={page + 1}
            onPageChange={(p) => setPage(p - 1)}
            itemLabel="đơn in"
          />
        </>
      )}
    </div>
  );
}

function FilterChip({
  label,
  count,
  color,
  active,
  onClick,
}: {
  label: string;
  count: number;
  color?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
        active ? "border-transparent bg-foreground text-background" : "hover:bg-muted/60"
      }`}
    >
      {color && !active ? (
        <span
          aria-hidden
          className="inline-block size-1.5 rounded-full"
          style={{ background: color }}
        />
      ) : null}
      {label}
      <span className="tabular-nums">{count}</span>
    </button>
  );
}
